import React from 'react';
import { Header } from '../Header';
import { Footer } from '../Footer';
import { BookingCard } from '../BookingCard';
import { FeaturedTestimonial } from '../FeaturedTestimonial';
import { PageHero } from './PageHero';
import { CheckList } from './CheckList';
import { ProseSection } from './ProseSection';
import { ProcessSteps } from './ProcessSteps';
import { PullQuote } from './PullQuote';
import { FaqSection, type Faq } from './FaqSection';
import { RelatedServices } from './RelatedServices';
import { SectionDivider, SURFACE } from './SectionDivider';
import { useSeo } from '../../lib/useSeo';
import { serviceSchema, faqSchema } from '../../lib/schema';
import { getOtherServices, type Service } from '../../data/services';
import { testimonials } from '../../data/testimonials';

/**
 * PAGE TEMPLATE: service detail
 * WORDPRESS: `single-service.php`
 *
 * The three service pages (Meditation Between Worlds, Sound-Infused Renewal,
 * Sopotje) were ~320 lines of near-identical JSX each. They are now one
 * template driven entirely by the `service` record, which is exactly the shape
 * a WordPress single-CPT template consumes.
 *
 * Block order (mirrors the ACF flexible-content rows):
 *   hero → checklist → prose[] → pull_quote? → featured_testimonial →
 *   process_steps → secondary_checklist? → faq → [booking sidebar] →
 *   related_services
 */

interface ServicePageTemplateProps {
  service: Service;
}
export function ServicePageTemplate({
  service
}: ServicePageTemplateProps) {
  const otherServices = getOtherServices(service.id);
  const testimonial = testimonials.find(item => item.id === service.featuredTestimonialId);
  useSeo({
    title: service.seo.title,
    description: service.seo.description,
    path: service.link,
    image: service.image,
    breadcrumbs: [{
      name: 'Start Your Journey',
      path: '/start-your-journey'
    }, {
      name: service.shortTitle,
      path: service.link
    }],
    schema: [serviceSchema({
      name: service.title,
      description: service.summary,
      path: service.link,
      image: service.image,
      price: service.priceNumeric,
      serviceType: service.shortTitle
    }), faqSchema(service.faqs as readonly Faq[] as Faq[])]
  });
  return <div className="min-h-screen bg-cream" data-id="element-181" data-mp="19wschawezjh1">
      <Header data-id="element-182" data-mp="1eu22osrmnz5x" />

      <main data-id="element-183" data-mp="1esh6yztkua6h">
        <PageHero eyebrow={service.eyebrow} heading={service.shortTitle} subheading={service.tagline} body={service.summary} bodyPanel align="center" image={service.image} imageAlt={service.imageAlt} size="tall" dividerFill={SURFACE.cream} data-id="element-184" data-mp="1et8zg1rvnrsj" />

        <section className="bg-cream px-6 py-20 md:py-28" data-id="element-185" data-mp="w3u2aifbbmtf">
          <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-12 lg:gap-16" data-id="element-186" data-mp="w17v4jwc39bq">
            <div className="space-y-20 lg:col-span-8" data-id="element-187" data-mp="22iuymp89270j">
              <CheckList {...service.checklist} headingId="right-for-you" data-id="element-188" data-mp="27hy5mf7sinb5" />

              {service.sections.map(section => <ProseSection key={section.id} {...section} data-id="element-189" data-mp="3n1fawea36kc5" />)}

              {service.pullQuote && <PullQuote {...service.pullQuote} data-id="element-190" data-mp="3iap6b39hilii" />}

              {testimonial && <FeaturedTestimonial testimonial={testimonial} data-id="element-191" data-mp="3i8ce7xa0iurq" />}

              <ProcessSteps eyebrow={service.steps.eyebrow} heading={service.steps.heading} headingId="what-to-expect" intro={service.steps.intro} steps={service.steps.items} data-id="element-192" data-mp="3dbc8jnh2ru15" />

              {service.secondaryChecklist && <CheckList {...service.secondaryChecklist} headingId="secondary-checklist" data-id="element-193" data-mp="2zdnxf7ct819a" />}

              <FaqSection faqs={service.faqs} data-id="element-194" data-mp="xj583gd5rfnu" />
            </div>

            <aside className="lg:col-span-4" data-id="element-195" data-mp="skvaz07vi4zl">
              <BookingCard price={service.price} priceDetail={service.priceDetail} duration={service.duration} format={service.format} data-id="element-196" data-mp="sm6csuhrd2o2" />
            </aside>
          </div>
        </section>

        <SectionDivider from={SURFACE.cream} to={SURFACE.creamDeep} className="mt-12" data-id="element-197" data-mp="3cfypp64f7n9g" />

        <RelatedServices services={otherServices} data-id="element-198" data-mp="3hcp29jh0o0q4" />
      </main>

      <Footer data-id="element-199" data-mp="32p3yrog8bzho" />
    </div>;
}